import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell, { BrandMark } from "@/components/AppShell";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Rocket } from "lucide-react";
import api from "@/lib/api";
import IndustryChips from "@/components/IndustryChips";
import StartupCard from "@/components/StartupCard";

const STAGES = [
  { value: "all", label: "All stages" },
  { value: "seed", label: "Seed" },
  { value: "series_a", label: "Series A" },
  { value: "series_b", label: "Series B" },
  { value: "series_c", label: "Series C" },
  { value: "growth", label: "Growth" },
  { value: "public", label: "Public" },
];

export default function Startups() {
  const { user } = useAuth();
  const [industries, setIndustries] = useState([]);
  const [stats, setStats] = useState(null);
  const [industry, setIndustry] = useState("all");
  const [stage, setStage] = useState("all");
  const [q, setQ] = useState("");
  const [startups, setStartups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [ind, st] = await Promise.all([
        api.get("/network/industries"),
        api.get("/network/stats"),
      ]);
      setIndustries(ind.data.industries);
      setStats(st.data);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = {};
      if (industry !== "all") params.industry = industry;
      if (stage !== "all") params.stage = stage;
      if (q) params.q = q;
      const { data } = await api.get("/startups", { params });
      setStartups(data);
      setLoading(false);
    })();
  }, [industry, stage, q]);

  const chipItems = useMemo(() => {
    if (!stats) return industries.map((i) => ({ key: i.key, label: i.label }));
    const counts = Object.fromEntries((stats.by_industry || []).map((b) => [b.industry, b.startups]));
    return industries.map((i) => ({ key: i.key, label: i.label, count: counts[i.key] || 0 }));
  }, [industries, stats]);

  const isAuthed = !!user && user !== false;

  const body = (
    <StartupsBody
      isAuthed={isAuthed}
      stats={stats}
      chipItems={chipItems}
      industry={industry}
      setIndustry={setIndustry}
      stage={stage}
      setStage={setStage}
      q={q}
      setQ={setQ}
      loading={loading}
      startups={startups}
    />
  );

  if (isAuthed) {
    return (
      <AppShell
        pageTitle="Startup Directory"
        pageDescription="India's healthcare startups — mapped across Digital Health, MedTech, Biotech, Health AI, and more."
      >
        {body}
      </AppShell>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <div className="flex items-center gap-2">
            <Link to="/network"><Button variant="ghost" className="hover:bg-primary/5">Network</Button></Link>
            <Link to="/directory"><Button variant="ghost" className="hover:bg-primary/5">Directory</Button></Link>
            <Link to="/login"><Button className="rounded-md" data-testid="nav-signin">Sign in</Button></Link>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 lg:pt-14">
        <div className="max-w-3xl">
          <div className="overline">Directory · Healthcare startups</div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl text-primary leading-tight">
            India&apos;s healthcare startup graph.
          </h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            {stats?.total_startups || 0} startups mapped across Digital Health, MedTech, Biotech, Health AI, Diagnostics, Pharma-tech and Insurance — with product lines, founders, funding stage and team size.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-8 pb-16">{body}</div>
      <Footer />
    </div>
  );
}

function StartupsBody({ isAuthed, stats, chipItems, industry, setIndustry, stage, setStage, q, setQ, loading, startups }) {
  return (
    <div className="space-y-8">
      <IndustryChips items={chipItems} value={industry} onChange={setIndustry} testidPrefix="startup-industry" />

      <div className="rounded-lg border border-border bg-white p-4 md:p-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-8">
            <label className="overline">Search</label>
            <div className="mt-1 relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Search by name or tagline…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="startup-search-input" />
            </div>
          </div>
          <div className="md:col-span-4">
            <label className="overline">Stage</label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger className="mt-1" data-testid="startup-stage-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-56 rounded-lg border border-border bg-white animate-pulse" />)}
        </div>
      ) : startups.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-10 text-center">
          <Rocket size={28} className="mx-auto text-muted-foreground" />
          <div className="mt-3 font-serif text-xl text-primary">No startups match these filters</div>
          <p className="text-sm text-muted-foreground mt-1">Try clearing the industry or stage filter.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="startups-grid">
          {startups.map((s) => <StartupCard key={s.id} startup={s} />)}
        </div>
      )}

      {isAuthed && (
        <Link
          to="/network"
          className="block rounded-xl border border-border bg-primary text-primary-foreground p-6 md:p-8 hover:opacity-95 transition-opacity"
          data-testid="cta-network"
        >
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <div className="overline text-primary-foreground/70">Network</div>
              <h2 className="mt-2 font-serif text-2xl md:text-3xl leading-tight">Track leaders behind these startups.</h2>
              <p className="mt-2 text-sm text-primary-foreground/80 max-w-xl">
                Explore career moves of {stats?.total_leaders || 0}+ senior healthcare leaders in India.
              </p>
            </div>
            <span className="rounded-md bg-white text-primary text-sm font-medium px-4 py-2.5 inline-flex items-center">
              Open Healthcare Network
            </span>
          </div>
        </Link>
      )}
    </div>
  );
}
